/**
 * Chiamate alle API dei template di sito pubblicati in anteprima.
 *
 * I template (Fondly, AdminPulse, ChatMind...) girano su sottodomini
 * `tpl-<id>.institorai.app` visibili solo a chi ha fatto l'accesso. Il gateway elenca quelli
 * pubblicati e, per aprirne uno, emette un **ticket monouso** da 30 secondi
 * dentro l'URL di ingresso del sottodominio: il cookie della dashboard non
 * attraversa i sottodomini, e il ticket è il modo di portarci la sessione senza
 * mettere un token nell'indirizzo (CLAUDE.md §10.4).
 *
 * Dal 2026-10-04 (ADR 0026) ogni template ha una fascia: `free` per tutti,
 * `subscription` solo per chi ha un piano Base o Premium. Se il template è
 * chiuso all'utente lo dice il server con `locked`; la pagina non ricalcola
 * nulla dal piano, e l'apertura di un template chiuso risponde 403 con codice
 * `subscription_required`.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3),
 * presidiati da `templates.test.ts`.
 */

import type { ApiClient } from '../client';

/** Fascia di un template: per tutti o riservato agli abbonati. */
export type TemplateTier = 'free' | 'subscription';

/** Codice del 403 con cui il gateway rifiuta l'apertura di un template chiuso. */
export const SUBSCRIPTION_REQUIRED = 'subscription_required';

/** Un template pubblicato in anteprima. */
export interface PublishedTemplate {
  /** Identificativo del template, es. `fondly-web`; coincide con la cartella in `templates/`. */
  id: string;
  /** Indirizzo pubblico dell'anteprima, es. `https://tpl-fondly-web.institorai.app/`. */
  url: string;
  /** Fascia del template. */
  tier: TemplateTier;
  /** True se il template è riservato agli abbonati e l'utente è sul piano gratuito. */
  locked: boolean;
}

/** Risposta dell'elenco dei template pubblicati. */
export interface TemplateList {
  /** Template pubblicati; vuoto quando le anteprime non sono attive. */
  templates: PublishedTemplate[];
}

/** Ingresso in un'anteprima, valido una volta sola. */
export interface TemplateEntry {
  /** URL di ingresso con il ticket monouso nella query string. */
  url: string;
  /** Scadenza del ticket, ISO 8601 (30 secondi dall'emissione). */
  expires_at: string;
}

/** Operazioni sui template, esposte alla UI. */
export class TemplatesApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenca i template pubblicati.
   *
   * @returns Gli identificativi pubblicati con il loro indirizzo.
   * @throws ApiError 401 senza sessione.
   */
  listTemplates(): Promise<TemplateList> {
    return this.client.request<TemplateList>('GET', '/api/v1/templates');
  }

  /**
   * Chiede l'ingresso in un'anteprima.
   *
   * @param id Identificativo del template.
   * @returns L'URL di ingresso con il ticket e la sua scadenza.
   * @throws ApiError 404 se il template non è pubblicato; 403 con codice
   *   `subscription_required` se è riservato agli abbonati e l'utente non lo è.
   */
  openTemplate(id: string): Promise<TemplateEntry> {
    return this.client.request<TemplateEntry>(
      'POST',
      `/api/v1/templates/${encodeURIComponent(id)}/open`,
    );
  }
}
